/**
 * CSV Export Utility
 *
 * Shared helpers for building and downloading CSV files.
 * Properly escapes cell values containing commas, quotes, or newlines.
 */

/**
 * Escapes a single CSV cell value.
 * Wraps in double-quotes if the value contains commas, quotes, or newlines.
 */
export function escapeCSVCell(value: unknown): string {
  const str = value === null || value === undefined ? "" : String(value);
  if (/[",\n\r]/.test(str)) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

/**
 * Neutralises spreadsheet formula injection.
 *
 * Excel and Sheets evaluate any cell whose text begins with =, +, -, @, tab or
 * carriage return, regardless of quoting. A contract or product name is
 * attacker-influenced free text, so it must be prefixed before it reaches a
 * spreadsheet. Apply to TEXT fields only -- prefixing a numeric column would
 * turn "-3.20" into text and break the pivot it exists for.
 */
export function sanitizeCSVText(value: string): string {
  if (value === "") return value;
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

/**
 * Builds a CSV string from headers and rows.
 */
export function buildCSV(headers: string[], rows: string[][]): string {
  const headerLine = headers.map(escapeCSVCell).join(",");
  const dataLines = rows.map((row) => row.map(escapeCSVCell).join(","));
  return [headerLine, ...dataLines].join("\n");
}

// UTF-8 byte order mark, written as an escape so it stays visible in source.
const UTF8_BOM = "\uFEFF";

/**
 * Triggers a browser file download for the given content.
 *
 * `bom` prepends a UTF-8 byte order mark, which Excel needs in order to read
 * non-ASCII contract names correctly. Defaults to false so existing callers
 * produce byte-identical output.
 */
export function triggerCSVDownload(
  csv: string,
  filename: string,
  options?: { bom?: boolean }
): void {
  const parts = options?.bom ? [UTF8_BOM, csv] : [csv];
  const blob = new Blob(parts, { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
